export type BillStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

export interface Bill {
  id?: number;
  created_at?: Date;
  subscription_id: number;
  organization_id?: string;
  total_amount: number;
  status: BillStatus;
  due_date: string;
  confirmed_at?: Date | null;
  cancelled_at?: Date | null;
  proof_payment_path?: string | null;
  subscriptions?: {
    id: string;
    cost: number;
    periodicity: string;
    subscribers?: {
      id: number;
      name: string;
      email?: string;
    };
  };
}

interface ApiResponse<T = any> {
  message: string;
  data: T;
  status: number;
}

class BillService {
  private baseUrl: string;

  constructor(baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const token = document.cookie.split('sgo_access_token=')[1]?.split(';')[0];
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async getBills(): Promise<Bill[]> {
    const response = await this.request<Bill[]>('/bills', {
      method: 'GET',
    });
    return response.data;
  }

  async getBill(id: number): Promise<Bill> {
    const response = await this.request<Bill>(`/bills/${id}`, {
      method: 'GET',
    });
    return response.data;
  }

  async updateBill(id: number, updates: Partial<Bill>): Promise<Bill> {
    const response = await this.request<Bill>(`/bills/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data;
  }

  async deleteBill(id: number): Promise<void> {
    await this.request<null>(`/bills/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadProofPayment(id: number, file: File): Promise<{ bill: Bill; proof_url: string }> {
    const token = document.cookie.split('sgo_access_token=')[1]?.split(';')[0];
    const formData = new FormData();
    formData.append('proof_payment', file);

    const response = await fetch(`${this.baseUrl}/bills/${id}/proof-payment`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Erro ao fazer upload');
    }

    return data.data;
  }

  async getProofPaymentUrl(id: number): Promise<string> {
    const response = await this.request<{ proof_url: string }>(`/bills/${id}/proof-payment`, {
      method: 'GET',
    });
    return response.data.proof_url;
  }
}

const billService = new BillService();
export default billService;
